import i18n, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { collections } from './collections';
import testLanguages from './testLanguages';

// Fake translation object for translation extraction
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function fakeT(key: string, _ns: unknown) {
  return key;
}

const directories = collections;

export const languages = [...testLanguages];

const FALLBACK_LANGUAGE = 'en-US';

const normalizeLanguage = (language: string) => {
  if (languages.includes(language)) return language;

  const languageCode = language.split('-')[0];
  if (languageCode === 'en') return FALLBACK_LANGUAGE;

  return languages.includes(languageCode) ? languageCode : undefined;
};

const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return FALLBACK_LANGUAGE;

  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];

  const matchedLanguage = browserLanguages
    .map(normalizeLanguage)
    .find((language): language is string => Boolean(language));

  return matchedLanguage ?? FALLBACK_LANGUAGE;
};

export let resources: Resource = {};

export const initI18n = async () => {
  // Detect language first, then load only en-US (fallback) + detected language
  const detectedLanguage = getBrowserLanguage();
  const languagesToLoad = [
    ...new Set([FALLBACK_LANGUAGE, detectedLanguage].filter((lng) => languages.includes(lng))),
  ];

  const translationPromises = languagesToLoad.flatMap((languageKey) =>
    directories.map(async (fileKey) => {
      let file: Record<string, unknown>;
      try {
        file = await import(
          /* @vite-ignore */
          languageKey === 'en-US'
            ? `./${languageKey}/${fileKey}.json`
            : `./@smartcat/${languageKey}/${fileKey}.json`
        ).then(({ default: myData }) => myData);
      } catch {
        if (languageKey !== 'en-US') {
          file = await import(
            /* @vite-ignore */
            `./en-US/${fileKey}.json`
          ).then(({ default: myData }) => myData);
        } else {
          throw new Error(`Missing required i18n file: en-US/${fileKey}.json`);
        }
      }
      return { languageKey, fileKey, file };
    })
  );

  const translationResults = await Promise.all(translationPromises);

  // Build the resources object from parallel results
  resources = translationResults.reduce<Resource>((acc, { languageKey, fileKey, file }) => {
    if (!acc[languageKey]) {
      acc[languageKey] = {};
    }
    acc[languageKey][fileKey] = file;
    return acc;
  }, {});

  await i18n.use(initReactI18next).init({
    lng: detectedLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    ns: ['common'],
    defaultNS: 'common',
    debug: true,
    resources,
    returnNull: false,
    interpolation: { escapeValue: false },
  });
};

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export default i18n;
